import { PawPrint } from "lucide-react";
import { DIMS, PC, STATUS } from "@/components/compass/dims";

const N = DIMS.length, STEP = 360 / N, C = 160;
const pt = (r, a) => [C + r * Math.sin((a * Math.PI) / 180), C - r * Math.cos((a * Math.PI) / 180)];
const seg = (i, ro = 142, ri = 58) => {
  const a0 = i * STEP - STEP / 2 + 1.5, a1 = a0 + STEP - 3;
  const [x0, y0] = pt(ro, a0), [x1, y1] = pt(ro, a1), [x2, y2] = pt(ri, a1), [x3, y3] = pt(ri, a0);
  return `M${x0},${y0} A${ro},${ro} 0 0 1 ${x1},${y1} L${x2},${y2} A${ri},${ri} 0 0 0 ${x3},${y3} Z`;
};

export function InteractiveCompass({ selected, onSelect, statuses, size = 360, tid = "compass-dial" }) {
  const idx = Math.max(0, DIMS.findIndex((d) => d.id === selected));
  const key = (e, i) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(DIMS[i].id); }
    if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); onSelect(DIMS[(i + 1) % N].id); }
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); onSelect(DIMS[(i + N - 1) % N].id); }
  };
  return (
    <svg viewBox="0 0 320 320" width="100%" style={{ maxWidth: size }} className="mx-auto block select-none" data-testid={tid} role="group" aria-label="Pet Compass: seven wellbeing dimensions">
      <circle cx={C} cy={C} r={156} fill={PC.paper} stroke={PC.line} />
      <g className="pc-spin-slow" style={{ transformOrigin: "160px 160px" }}>
        {Array.from({ length: 56 }).map((_, k) => { const [a, b] = pt(152, k * 6.43), [c2, d] = pt(k % 8 === 0 ? 144 : 148, k * 6.43); return <line key={k} x1={a} y1={b} x2={c2} y2={d} stroke={PC.sage} strokeOpacity="0.5" strokeWidth={k % 8 === 0 ? 1.6 : 0.8} />; })}
      </g>
      {DIMS.map((d, i) => {
        const on = d.id === selected;
        const st = statuses ? STATUS[statuses[d.id] || "unknown"] : null;
        const fill = st ? st.bg : d.color;
        const [ix, iy] = pt(100, i * STEP);
        const Icon = d.icon;
        return (
          <g key={d.id} role="button" tabIndex={0} aria-pressed={on} aria-label={`${d.label}${st ? `: ${st.label}` : ""}`}
            onClick={() => onSelect(d.id)} onKeyDown={(e) => key(e, i)} className="cursor-pointer outline-none [&:focus-visible>path]:stroke-[#1F1B18]" data-testid={`${tid}-seg-${d.id}`}>
            <path d={seg(i)} fill={fill} fillOpacity={st ? 1 : on ? 0.95 : 0.22} stroke={on ? (st ? st.fg : d.color) : "#fff"} strokeWidth={on ? 3 : 2}
              style={{ transition: "fill-opacity 300ms ease, stroke 300ms ease" }} />
            <foreignObject x={ix - 13} y={iy - 13} width="26" height="26" pointerEvents="none">
              <Icon width={26} height={26} color={st ? st.fg : on ? "#fff" : d.color} strokeWidth={2.2} />
            </foreignObject>
          </g>
        );
      })}
      <g style={{ transform: `rotate(${idx * STEP}deg)`, transformOrigin: "160px 160px" }} className="pc-needle" aria-hidden="true">
        <path d="M160,40 L170,150 L160,160 L150,150 Z" fill={PC.coral} />
        <path d="M160,280 L170,170 L160,160 L150,170 Z" fill={PC.deep} fillOpacity="0.25" />
      </g>
      <circle cx={C} cy={C} r={46} fill={PC.cream} stroke={PC.line} />
      <foreignObject x={C - 16} y={C - 22} width="32" height="32" pointerEvents="none"><PawPrint width={32} height={32} color={PC.deep} /></foreignObject>
      <text x={C} y={C + 24} textAnchor="middle" fontSize="9" fontWeight="800" fill={PC.deep} letterSpacing="1.5">COMPASS</text>
    </svg>
  );
}

export function DimChips({ selected, onSelect, statuses, tid = "compass-chip" }) {
  return (
    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Choose a dimension">
      {DIMS.map((d, i) => {
        const on = d.id === selected, st = statuses ? STATUS[statuses[d.id] || "unknown"] : null;
        return (
          <button key={d.id} role="tab" aria-selected={on} onClick={() => onSelect(d.id)} data-testid={`${tid}-${d.id}`}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-3.5 text-xs font-bold transition-colors"
            style={{ background: on ? (st ? st.fg : d.color) : "#fff", color: on ? "#fff" : PC.ink, border: `1.5px solid ${on ? "transparent" : PC.line}` }}>
            <span className="grid h-5 w-5 place-items-center rounded-full text-[10px]" style={{ background: on ? "rgba(255,255,255,.25)" : (st ? st.bg : `${d.color}22`), color: on ? "#fff" : (st ? st.fg : d.color) }}>{i + 1}</span>
            {d.label}
          </button>
        );
      })}
    </div>
  );
}
